import { http } from '@/api/http'
import { toListParams, type ListQuery } from '@/api/query'
import type { ListResponse, WarehouseCell, WarehouseLot, WarehouseLotMovement } from '@/types/api'
import type { RequestOptions } from '../crud'

/** A read-only stock view: stock changes only through warehouse documents. */
function readOnlyList<T extends { id: string }>(path: string) {
  const base = `/${path}`
  const list = async (query?: ListQuery, options?: RequestOptions) => {
    const { data } = await http.get<ListResponse<T>>(base, { params: toListParams(query), ...options })
    return { count: data.count ?? 0, items: data.items ?? [] }
  }
  return {
    path,
    list,
    /** There is no single-row endpoint; the row is found by id in the list. */
    async get(id: string, options?: RequestOptions) {
      const result = await list({ filters: [{ field: 'id', type: 'eq', value: id }], limit: 1 }, options)
      if (!result.items[0]) throw new Error('not found')
      return result.items[0]
    },
  }
}

export const warehouseCellsService = readOnlyList<WarehouseCell>('warehouse/cells')
export const warehouseLotsService = {
  ...readOnlyList<WarehouseLot>('warehouse/lots'),
  /** Document lines that changed one lot, in document order (Ucode's stock-item relation). */
  async movements(lotID: string, options?: RequestOptions) {
    const { data } = await http.get<WarehouseLotMovement[]>(`/warehouse/lots/${encodeURIComponent(lotID)}/movements`, options)
    return data ?? []
  },
}
